import Mime from 'mime/lite';
import { ApiCall } from './ApiClient';
import {
    InvitationData, ResultData
} from './ApiTypes';
import { dateTransformer } from './ApiUtil';

const get = <TResponse>(def: Omit<ApiCall<never, TResponse>, 'method'>): ApiCall<never, TResponse> =>
    Object.assign(def, { method: 'get' });

const post = <TResponse>(def: Omit<ApiCall<any, TResponse>, 'method'>): ApiCall<any, TResponse> =>
    Object.assign(def, { method: 'post' });

const put = <TResponse>(def: Omit<ApiCall<any, TResponse>, 'method'>): ApiCall<any, TResponse> =>
    Object.assign(def, { method: 'put' });

const required = <T>(name: string, value: T) => {
    // If we use isNaN it returns false for strings, etc so it screws up what we are trying to do
    // tslint:disable: use-isnan
    // eslint-disable-next-line
    if (value === null || value === undefined || (value as any) === Infinity || isNaN(value as any)) {
        throw new Error(`api failure: ${name} is a required parameter`);
    }
    return value;
};

export const getAccessToken = (via: string) => post<{ accessToken: string }>({
    path: '/request_access',
    body: { via }
});

export const getPermitToken = (podId: string, type: 'account' | 'board') => post<{ permitToken: string }>({
    path: '/request_permit',
    body: { podId, type }
});

export const getInviteList = (objectType: 'account', objectId: string) => get<InvitationData[]>({
    path: '/invites.list',
    query: { objectType, objectId },
    transform: dateTransformer(),
});

export const createInviteForAccount = (accountId: string, expiry: string) => post<InvitationData>({
    path: '/invites.createForAccount',
    body: { accountId, expiry },
    transform: dateTransformer(),
});

export const deleteInvite = (invitationId: string) => post<{ ok: boolean }>({
    path: '/invites.delete',
    body: { invitationId },
});

export const getUserPicture = (userId: string) => get<any>({
    path: `/users/${required('userId', userId)}/photo`,
});

export const putTemplate = (boardId: string) => post<ResultData>({
    path: '/templates',
    body: { boardId, action: 'set' }
});

export const deleteTemplate = (boardId: string) => post<ResultData>({
    path: '/templates',
    body: { boardId, action: 'unset' }
});

export const postFileAsBlob = (file: Blob | File) => post<{ url: string }>({
    path: '/files',
    body: file,
    headers: { 'X-Filename': 'name' in file ? file.name : `file.${Mime.getExtension(file.type)}` },
});

export const postFileViaLink = (url: string) => post<{ url: string }>({
    path: '/files',
    body: { url },
});

export const resolveImage = (url: string) => post<{ url: string }>({
    path: '/util/resolve-image',
    body: { url },
});
